import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  NotificationItem, NotifySeverity, dismissAllNotifications, dismissNotification,
  getSnapshot, removeNotification, subscribe
} from './notificationStore';

const BORDER = '#e2e8f0';
const DARK = '#1e293b';
const TEXT = '#475569';
const MUTED = '#94a3b8';

const COLORS: Record<NotifySeverity, string> = {
  success: '#2e7d32',
  error: '#d32f2f',
  warning: '#ed6c02',
  info: '#1976d2'
};

const ICONS: Record<NotifySeverity, React.ElementType> = {
  success: CheckCircleRoundedIcon,
  error: ErrorRoundedIcon,
  warning: WarningAmberRoundedIcon,
  info: InfoRoundedIcon
};

/** Куда прижата «шторка». Чтобы перенести в другой угол, достаточно поменять эти значения */
const EDGE_GAP = 16;
const STACK_WIDTH = 380;
/** Выше диалогов (1300), штатных snackbar (1400) и подсказок (1500) */
const Z_INDEX = 1600;

const SLIDE = 'calc(100% + 40px)';
const enterRow = keyframes`from { grid-template-rows: 0fr; } to { grid-template-rows: 1fr; }`;
const enterCard = keyframes`from { transform: translateX(${SLIDE}); opacity: 0; } 60% { opacity: 1; } to { transform: translateX(0); opacity: 1; }`;
const leaveRow = keyframes`from { grid-template-rows: 1fr; } to { grid-template-rows: 0fr; }`;
const leaveCard = keyframes`from { transform: translateX(0); opacity: 1; } to { transform: translateX(${SLIDE}); opacity: 0; }`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const fadeOut = keyframes`from { opacity: 1; } to { opacity: 0; }`;
const drain = keyframes`from { transform: scaleX(1); } to { transform: scaleX(0); }`;

/** Страница в фоновой вкладке: таймеры стоят, чтобы пользователь не пропустил уведомление */
function usePageHidden(): boolean {
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  useEffect(() => {
    const onChange = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return hidden;
}

interface CardProps {
  item: NotificationItem;
  pageHidden: boolean;
}

const NotificationCard: React.FC<CardProps> = ({ item, pageHidden }) => {
  const [hover, setHover] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const color = COLORS[item.severity];
  const Icon = ICONS[item.severity];
  const timed = item.duration > 0;
  const paused = hover || pageHidden;

  // Подстраховка: если анимация ухода не сработала (reduced motion, скрытая вкладка), всё равно убираем уведомление
  useEffect(() => {
    if (!item.leaving) return undefined;
    const timer = window.setTimeout(() => removeNotification(item.uid), 600);
    return () => window.clearTimeout(timer);
  }, [item.leaving, item.uid]);

  return (
    <Box
      ref={rowRef}
      onAnimationEnd={e => { if (item.leaving && e.target === rowRef.current) removeNotification(item.uid); }}
      sx={{
        display: 'grid',
        gridTemplateRows: item.leaving ? '0fr' : '1fr',
        animation: `${item.leaving ? leaveRow : enterRow} 260ms ease-out ${item.leaving ? '80ms' : '0ms'} both`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
      }}
    >
      {/* overflow-y: clip схлопывает строку по высоте, не обрезая слайд карточки по горизонтали */}
      <Box sx={{ minHeight: 0, overflowX: 'visible', overflowY: 'clip', pointerEvents: 'none' }}>
        <Box sx={{ py: 0.75, pointerEvents: 'none' }}>
          <Box
            role={item.severity === 'error' ? 'alert' : 'status'}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocusCapture={() => setHover(true)}
            onBlurCapture={() => setHover(false)}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              pointerEvents: 'auto',
              bgcolor: '#fff',
              border: `1px solid ${BORDER}`,
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(15,23,42,.08), 0 6px 16px rgba(15,23,42,.08)',
              animation: item.leaving
                ? `${leaveCard} 260ms cubic-bezier(.4,0,1,1) both`
                : `${enterCard} 380ms cubic-bezier(.16,1,.3,1) both`,
              '@media (prefers-reduced-motion: reduce)': {
                animation: `${item.leaving ? fadeOut : fadeIn} 140ms linear both`
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, pl: 1.5, pr: 0.75, pt: 1.25, pb: timed ? 1.5 : 1.25 }}>
              <Icon sx={{ fontSize: 20, color, mt: '1px', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {item.title && (
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: DARK, lineHeight: 1.35, wordBreak: 'break-word' }}>
                    {item.title}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: 13, color: item.title ? TEXT : DARK, fontWeight: item.title ? 400 : 500, lineHeight: 1.45,
                    wordBreak: 'break-word', whiteSpace: 'pre-line', maxHeight: 168, overflowY: 'auto'
                  }}
                >
                  {item.message}
                  {item.count > 1 && (
                    <Box
                      component="span"
                      sx={{ ml: 0.75, px: 0.75, py: '1px', borderRadius: 10, bgcolor: '#f1f5f9', color: '#64748b', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      ×{Math.min(item.count, 99)}
                    </Box>
                  )}
                </Typography>
                {item.action && (
                  <Button
                    size="small"
                    onClick={() => { item.action?.onClick(); dismissNotification(item.uid); }}
                    sx={{ mt: 0.5, ml: -0.75, minWidth: 0, px: 0.75, py: 0.25, fontSize: 12.5, fontWeight: 600, textTransform: 'none', color }}
                  >
                    {item.action.label}
                  </Button>
                )}
              </Box>
              <IconButton
                size="small" aria-label="Закрыть уведомление"
                onClick={() => dismissNotification(item.uid)}
                sx={{ color: MUTED, p: 0.5, '&:hover': { color: DARK, bgcolor: '#f1f5f9' } }}
              >
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {timed && !item.leaving && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, bgcolor: '#f1f5f9' }}>
                <Box
                  key={item.version}
                  ref={barRef}
                  onAnimationEnd={e => { if (e.target === barRef.current) dismissNotification(item.uid); }}
                  sx={{
                    height: '100%', bgcolor: color, opacity: 0.85, transformOrigin: 'left center',
                    animation: `${drain} ${item.duration}ms linear forwards`,
                    animationPlayState: paused ? 'paused' : 'running'
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/** «Шторка» уведомлений: монтируется один раз в App. Новые уведомления появляются снизу, старые поднимаются выше */
const NotificationHost: React.FC = () => {
  const items = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const pageHidden = usePageHidden();
  const alive = items.filter(item => !item.leaving).length;

  if (!items.length || typeof document === 'undefined') return null;

  return createPortal(
    <Box
      role="region" aria-label="Уведомления"
      sx={{
        position: 'fixed', right: EDGE_GAP, bottom: EDGE_GAP - 6, zIndex: Z_INDEX,
        width: `min(${STACK_WIDTH}px, calc(100vw - ${EDGE_GAP * 2}px))`,
        display: 'flex', flexDirection: 'column', pointerEvents: 'none'
      }}
    >
      {alive > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pointerEvents: 'none', pr: 0.25, pb: 0.25, animation: `${fadeIn} 200ms ease-out both` }}>
          <Button
            size="small" onClick={dismissAllNotifications}
            sx={{ pointerEvents: 'auto', minWidth: 0, px: 1, py: 0.25, fontSize: 12, fontWeight: 600, textTransform: 'none', color: '#64748b', bgcolor: 'rgba(255,255,255,.92)', border: `1px solid ${BORDER}`, borderRadius: 10, '&:hover': { bgcolor: '#fff', color: DARK } }}
          >
            Закрыть все · {alive}
          </Button>
        </Box>
      )}
      {items.map(item => <NotificationCard key={item.uid} item={item} pageHidden={pageHidden} />)}
    </Box>,
    document.body
  );
};

export default NotificationHost;
